import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart } from './_finHelpers';

// VAT Calculator — solves for missing values given any two
export default function VATCalc() {
  const [netPrice, setNetPrice] = useState(1200);
  const [tax, setTax] = useState(96);
  const [gross, setGross] = useState(1296);
  const [rate, setRate] = useState(8);
  const [locked, setLocked] = useState({ net: true, rate: true }); // which two are provided
  const c = useMemo(() => {
    let n = netPrice, t = tax, g = gross, r = rate;
    if (locked.net && locked.rate) { t = n * r/100; g = n + t; }
    else if (locked.net && locked.tax) { r = (t / n) * 100; g = n + t; }
    else if (locked.net && locked.gross) { t = g - n; r = (t / n) * 100; }
    else if (locked.rate && locked.tax) { n = t / (r/100); g = n + t; }
    else if (locked.rate && locked.gross) { n = g / (1 + r/100); t = g - n; }
    else if (locked.tax && locked.gross) { n = g - t; r = (t / n) * 100; }
    return { net: n, tax: t, gross: g, rate: r };
  }, [netPrice, tax, gross, rate, locked]);

  return (
    <CalcShell look="cnet" title="VAT Calculator" subtitle="Value-added tax calculator. Provide any two of Net price, Tax amount, Gross price, and Rate."
      about={<><p><b>Value-Added Tax (VAT)</b> is a consumption tax placed on a product whenever value is added at each stage of the supply chain. This calculator solves for missing values from any two known ones.</p></>}
    >
      <p className="text-sm text-slate-600 mb-6">Please provide any two values from the following inputs to calculate the remaining values.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Net price <span className="text-xs">(before tax)</span></label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={netPrice} onChange={e=>setNetPrice(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Tax rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Tax amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={tax} onChange={e=>setTax(+e.target.value||0)} className="flex-1"/></div></div>
          <div className="field"><label>Gross price <span className="text-xs">(after tax)</span></label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={gross} onChange={e=>setGross(+e.target.value||0)} className="flex-1"/></div></div>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Result</h3>
          <table className="data-table"><tbody>
            <tr><td>Gross price</td><td className="text-right font-semibold text-lg text-blue-700" data-testid="vat-gross">{fmt(c.gross)}</td></tr>
            <tr><td>Net price</td><td className="text-right">{fmt(c.net)}</td></tr>
            <tr><td>Tax amount</td><td className="text-right text-orange-600">{fmt(c.tax)}</td></tr>
            <tr><td>Tax rate</td><td className="text-right">{c.rate.toFixed(2)}%</td></tr>
          </tbody></table>
          <div className="mt-6"><PieChart parts={[{label:'Net price', value:c.net, color:'#2563eb'},{label:'Tax amount', value:c.tax, color:'#10b981'}]} size={180}/></div>
        </div>
      </div>
    </CalcShell>
  );
}
